import { useState } from "react";
import { Share2, MessageCircle, Facebook, Copy } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

// Share button for static pages. `path` is the site path (e.g. "about", "programs/x");
// the shared link goes through /api/s/<path> so WhatsApp/Facebook get a rich preview.
export function ShareButton({ path, title, light = false, testid = "page-share-btn" }) {
  const { lang } = useLang();
  const [open, setOpen] = useState(false);
  const shareUrl = `${API}/s/${path.replace(/^\/+/, "")}`;
  const waUrl = `https://wa.me/?text=${encodeURIComponent(`${title} — ${shareUrl}`)}`;
  const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
  const shareLabel = { en: "Share", cs: "Sdílet", he: "שיתוף", ru: "Поделиться" }[lang] || "Share";
  const copiedLabel = { en: "Link copied", cs: "Odkaz zkopírován", he: "הקישור הועתק", ru: "Ссылка скопирована" }[lang] || "Link copied";

  const handleShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title, url: shareUrl }); } catch (e) { /* cancelled */ }
      return;
    }
    setOpen((o) => !o);
  };
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success(copiedLabel); } catch (e) { /* ignore */ }
    setOpen(false);
  };

  const btnCls = light
    ? "border-white/30 text-white hover:border-[#BE923C] hover:text-[#D8B45E] bg-white/10 backdrop-blur-md"
    : "border-[#0F172A]/15 text-[#0F172A] hover:border-[#BE923C] bg-white/70";

  return (
    <div className="relative inline-block">
      <button type="button" onClick={handleShare} data-testid={testid}
        className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${btnCls}`}>
        <Share2 size={16} /> {shareLabel}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className={`absolute end-0 w-48 z-50 rounded-xl border border-[#0F172A]/10 bg-white text-[#0F172A] shadow-xl overflow-hidden ${light ? "bottom-full mb-2" : "mt-2"}`} data-testid={`${testid}-menu`}>
            <a href={waUrl} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)} data-testid={`${testid}-whatsapp`}
              className="flex items-center gap-2.5 px-4 py-2.5 text-sm hover:bg-[#F9F8F6] transition-colors"><MessageCircle size={16} className="text-[#0F172A]" /> WhatsApp</a>
            <a href={fbUrl} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)} data-testid={`${testid}-facebook`}
              className="flex items-center gap-2.5 px-4 py-2.5 text-sm hover:bg-[#F9F8F6] transition-colors"><Facebook size={16} className="text-[#1877F2]" /> Facebook</a>
            <button type="button" onClick={copyLink} data-testid={`${testid}-copy`}
              className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm hover:bg-[#F9F8F6] transition-colors text-start"><Copy size={16} /> Copy link</button>
          </div>
        </>
      )}
    </div>
  );
}
